import {
  endOfMonth,
  endOfDay,
  format,
  differenceInMinutes,
  isEqual,
  sub,
  add,
  differenceInDays
} from 'date-fns';
import { formatToTimeZone, parseFromTimeZone } from 'date-fns-timezone';
import numeral from 'numeral';
import { UnitType } from '@/modules/costAnalysis/types';

export type CustomLocale = {
  currency: string;
  localeSymbol: string;
  currencySymbol: string;
};

const numeralLocales = [
  {
    locale: 'GBP',
    delimiters: { thousands: ',', decimal: '.' },
    abbreviations: {
      thousand: 'k',
      million: 'm',
      billion: 'bn',
      trillion: 'tn'
    },
    currency: { symbol: '£' }
  },
  {
    locale: 'USD',
    delimiters: { thousands: ',', decimal: '.' },
    abbreviations: {
      thousand: 'k',
      million: 'm',
      billion: 'bn',
      trillion: 'tn'
    },
    currency: { symbol: '$' }
  },
  {
    locale: 'ZAR',
    delimiters: { thousands: ',', decimal: '.' },
    abbreviations: {
      thousand: 'k',
      million: 'm',
      billion: 'bn',
      trillion: 'tn'
    },
    currency: { symbol: 'R' }
  },
  {
    locale: 'AUD',
    delimiters: { thousands: ',', decimal: '.' },
    abbreviations: {
      thousand: 'k',
      million: 'm',
      billion: 'bn',
      trillion: 'tn'
    },
    currency: { symbol: 'A$' }
  },
  {
    locale: 'EUR',
    delimiters: { thousands: ',', decimal: '.' },
    abbreviations: {
      thousand: 'k',
      million: 'm',
      billion: 'bn',
      trillion: 'tn'
    },
    currency: { symbol: '€' }
  }
];

numeralLocales.forEach(locale => {
  numeral.register('locale', locale.locale, {
    delimiters: locale.delimiters,
    abbreviations: locale.abbreviations,
    ordinal: num => {
      const b = num % 10;
      return Math.floor((num % 100) / 10) === 1
        ? 'th'
        : b === 1
        ? 'st'
        : b === 2
        ? 'nd'
        : b === 3
        ? 'rd'
        : 'th';
    },
    currency: locale.currency
  });
});

numeral.locale('GBP');
const EPSILON = 0.000001;
export default class Utils {
  // convert date to local time zone of user browser
  static convertDateToLocaleFormat(date: any) {
    if (typeof date != 'string') {
      date += '';
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const format = 'YYYY/MM/DD HH:mm:ss';
    const sourceDate = parseFromTimeZone(date, { timeZone: 'UTC' });
    const output = formatToTimeZone(sourceDate, format, { timeZone: timeZone });
    return output;
  }

  // Used in places where we want to only Millions
  static costInMillion(cost: number) {
    if (!cost) {
      return null;
    }
    return numeral(cost).format('0.(00)am');
  }

  static formatDate(
    date: string | Date | null | undefined,
    specifiedFormat: string = 'do MMM, yyyy',
    formatTz: boolean = true
  ) {
    if (!date) {
      return '';
    }
    let dateObj: Date;
    dateObj = typeof date === 'string' ? new Date(date) : date;

    if (!Utils.isValidDate(dateObj)) {
      if (typeof date !== 'string') {
        return '';
      }
      dateObj = new Date(date.replace(/-/g, '/'));
    }

    if (formatTz) {
      dateObj = new Date(this.convertDateToLocaleFormat(date));
    }

    if (dateObj.toString().includes('Invalid')) {
      return '';
    }

    return format(dateObj, specifiedFormat);
  }

  static isValidDate(d: any) {
    return d instanceof Date && !isNaN(d.getTime());
  }

  static getParsedDate = (date?: string): Date => {
    if (!date) {
      return new Date();
    }

    let dateObject = new Date(date);
    if (!Utils.isValidDate(dateObject)) {
      // One of the date formats used ('MM-DD-YYYY') is not parsed correctly in Firefox with new Date() constructor
      // so change format to YYYY-MM-DD and re-parse
      const splitDate = date.split('-');
      if (Array.isArray(splitDate) && splitDate.length === 3) {
        dateObject = new Date(
          [splitDate[2], splitDate[0], splitDate[1]].join('-')
        );
      }
    }

    if (Utils.isValidDate(dateObject)) {
      return dateObject;
    }

    return new Date();
  };

  static costFormatAll(labelValue?: number, includeCurrency = false) {
    if (this.isEmpty(labelValue)) {
      return null;
    }
    if (includeCurrency) {
      return numeral(labelValue).format('$0.00a');
    }
    return numeral(labelValue).format('0.00a');
  }

  static formatValueUsingCurrency(
    labelValue?: number,
    includeCurrency = false,
    currency: string = 'GBP'
  ) {
    if (this.isEmpty(labelValue)) {
      return null;
    }
    numeral.locale(currency);
    if (includeCurrency) {
      return numeral(labelValue).format('$0.00a');
    }
    return numeral(labelValue).format('0.00a');
  }

  static endOfMonth(date?: any) {
    if (date) {
      return endOfMonth(new Date(date));
    }
    return endOfMonth(new Date());
  }

  static endOfDay(date?: any) {
    if (date) {
      return endOfDay(new Date(date));
    }
    return endOfDay(new Date());
  }

  static differenceInDates(date1: Date, date2?: Date) {
    if (!date2) {
      date2 = new Date();
    }

    return differenceInMinutes(date1, date2);
  }

  static formatNumber(value: any, format = '0.0a') {
    if (!value) {
      return value;
    }
    if (Math.abs(value) < EPSILON) {
      value = numeral(0).format(format);
    }
    return numeral(value).format(format);
  }

  static formatByUnitType(
    value?: number,
    unit: string = 'gbp',
    format: string = '0.0'
  ) {
    if (!value) {
      return value;
    }
    const formats: any = {
      [UnitType.GBP]: '0.[00]a',
      [UnitType.PERCENT]: '0.0[0][0]%',
      [UnitType.BPS]: '0.[0][0]'
    };

    const frmt = formats[unit] || format;
    const formattedValue = Utils.formatNumber(value, frmt);

    if (unit === UnitType.BPS) {
      return `${formattedValue} BPS`;
    }

    return formattedValue;
  }

  static currencyFormatter(value: number) {
    if (!value) {
      return value;
    } else return new Intl.NumberFormat('en-GB').format(value);
  }

  /**
   * Checks whether a given value is empty of not.
   *
   * The value will be considered empty as follows:
   *
   * default: null | undefined
   *
   * string: ''
   * object: {}
   * array: []
   * @param value
   */
  static isEmpty(value?: any): boolean {
    if ([null, undefined].includes(value)) {
      return true;
    }

    if (typeof value === 'string') {
      return value === '';
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }

    return false;
  }

  /**
   * @see isEmpty
   */
  static isNotEmpty(value?: any): boolean {
    return !Utils.isEmpty(value);
  }

  static areDatesEqual(date1: Date, date2: Date): boolean {
    if (date1 instanceof Date && date2 instanceof Date) {
      return isEqual(date1, date2);
    }

    return false;
  }

  static addYearToDate(date: Date) {
    return this.formatDate(
      sub(add(date, { years: 1 }), { days: 1 }),
      'yyy-MM-dd'
    );
  }

  static omitAlphabetsFromString(text: string) {
    return text.replace(/[a-zA-Z]*/g, '');
  }

  static monthStringToNumber(month: string) {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    return months.indexOf(month);
  }

  static convertReportingPeriodStringToDate(reportingPeriod: string) {
    const splitPeriod = reportingPeriod.replace('-', '').split(/[ ,]+/);
    if (splitPeriod.length != 6) {
      return [];
    }
    return [
      new Date(
        parseInt(splitPeriod[2]),
        this.monthStringToNumber(splitPeriod[1]),
        parseInt(this.omitAlphabetsFromString(splitPeriod[0]))
      ),
      new Date(
        parseInt(splitPeriod[5]),
        this.monthStringToNumber(splitPeriod[4]),
        parseInt(this.omitAlphabetsFromString(splitPeriod[3]))
      )
    ];
  }

  static sortReportingPeriods(period1: string, period2: string) {
    const period1StartEnd = this.convertReportingPeriodStringToDate(period1);
    const period2StartEnd = this.convertReportingPeriodStringToDate(period2);
    if (period1StartEnd.length < 2) {
      return -1;
    }
    if (period2StartEnd.length < 2) {
      return 1;
    }
    if (period1StartEnd[0] < period2StartEnd[0]) {
      return -1;
    }
    if (period1StartEnd[0] > period2StartEnd[0]) {
      return 1;
    }
    if (period1StartEnd[1] < period2StartEnd[1]) {
      return -1;
    }
    if (period1StartEnd[1] > period2StartEnd[1]) {
      return 1;
    }
    return 0;
  }

  /** Converts byte to nearest highest unit . i.e. KB to be converted to MB, MB to GB ... */
  static upgradeUnitForByte(bytes: number) {
    return bytes / 1024;
  }

  static getUniqueIdentifierType(slug: string) {
    switch (slug) {
      case 'isin': {
        return 'ISIN';
        break;
      }
      case 'cg_issued': {
        return 'CG Issued ID';
        break;
      }
      case 'mandate-code': {
        return 'Mandate Code';
        break;
      }
      default: {
        return slug;
        break;
      }
    }
  }

  /**
   * Check Browser is Internet Explorer
   * @returns boolean
   */
  static detectIE(): boolean {
    const userAgent = window.navigator.userAgent;
    const ieRegEx = /MSIE|Trident|Edge/;

    if (ieRegEx.test(userAgent)) {
      return true;
    }

    return false;
  }

  /**
   * Returns current month
   * 01 January, 02 February ....
   * @returns number
   */
  static getCurrentMonth() {
    const todaysDate = new Date();
    return todaysDate.getMonth();
  }

  /**
   * Returns approximate number of months between two dates
   * @param start
   * @param end
   * @returns number
   */
  static calculateApproxDuration(start: Date, end: Date): number {
    // 1 day = 0.032855 mo
    return Math.round(differenceInDays(start, end) * 0.032855);
  }

  static isValidNumber = (value?: any): boolean => {
    return Number.isFinite(value);
  };

  static safeMultiply(num1: number, num2: number) {
    return Number.isFinite(num1) && Number.isFinite(num2)
      ? num1 * num2
      : undefined;
  }

  static safeDivide(numerator: number, denominator: number) {
    return Number.isFinite(numerator) && denominator
      ? numerator / denominator
      : undefined;
  }

  /**
   * Converts fraction to Percent value with fixed precision.
   *
   * eg: 0.67825 => 67.8
   * @param value
   * @param precision - Default: 2
   * @see isValidNumber
   * @category Formatting
   */
  static fractionToPercent(value: number, precision = 2): string {
    if (!Utils.isValidNumber(value)) {
      return '';
    }
    const percentValue = Utils.safeMultiply(value, 100);
    let outputNumber = '';
    let integer = percentValue?.toString()?.split('.')[0];
    if (!integer) {
      integer = '0';
    }
    if (precision < 0) {
      return integer;
    }
    let decimalPart = percentValue?.toString()?.split('.')[1];
    // Handle exponential
    if (percentValue?.toString()?.includes('e')) {
      outputNumber = percentValue.toFixed(precision);
      return outputNumber;
    }
    // If no decimal part or decimal part doesn't have enough values
    // add zeros to it.
    if (!decimalPart || decimalPart?.length < precision) {
      if (!decimalPart) {
        decimalPart = '';
      }
      for (let i = 0; i < precision; i++) {
        decimalPart += '0';
      }
    }
    // Take the required length of decimal part
    const formattedDecimal = decimalPart?.slice(0, precision);
    outputNumber = `${integer}.${formattedDecimal}`;
    return outputNumber;
  }
}

export function areUuidArraysEqual(
  array1: string[],
  array2: string[]
): boolean {
  if (array1.length !== array2.length) {
    return false;
  }

  const set1 = new Set(array1.map(uuid => uuid.toLowerCase()));
  const set2 = new Set(array2.map(uuid => uuid.toLowerCase()));

  if (set1.size !== set2.size) {
    return false;
  }

  for (const uuid of set1) {
    if (!set2.has(uuid)) {
      return false;
    }
  }

  return true;
}
